import { Workout, WorkoutExercise } from '@strength/domain-workout';
import { UnrecordedActual, WorkoutData } from './types';

// Get today's date in YYYY-MM-DD format without timezone issues
export const getTodayDateString = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export function categorizeWorkoutsByDate(athleteWorkouts: WorkoutData[]) {
  const today = getTodayDateString();

  // Separate dated vs flex (no assignedDate) workouts
  const datedWorkouts = athleteWorkouts.filter(
    (w): w is WorkoutData & { assignedDate: string } =>
      w.assignedDate !== null && w.assignedDate !== undefined,
  );

  const todayWorkouts = datedWorkouts.filter(
    (w) => w.assignedDate === today && w.status === 'planned',
  );
  const missingWorkouts = datedWorkouts.filter(
    (w) => w.assignedDate < today && w.status === 'planned',
  );
  const upcomingWorkouts = datedWorkouts.filter(
    (w) => w.assignedDate > today && w.status === 'planned',
  );

  // Flex: no assigned date, block allows flex workouts, still planned
  const flexWorkouts = athleteWorkouts.filter(
    (w) =>
      (w.assignedDate === null || w.assignedDate === undefined) &&
      w.block?.allowFlexWorkouts === true &&
      w.status === 'planned',
  );

  const sortByCycleAndSession = <T extends WorkoutData>(workouts: T[]) => {
    return workouts.sort((a, b) => {
      const aCycle = a.cycleSortOrder;
      const bCycle = b.cycleSortOrder;
      if (aCycle !== bCycle) return aCycle - bCycle;
      const aSession = a.sessionSortOrder;
      const bSession = b.sessionSortOrder;
      return aSession - bSession;
    });
  };

  // group the flex workouts by block and sort the workouts by session x cycle numbers ascending
  // first group by block and then sort the block groups in order of:
  //  1. current blocks (blocks whose date range spans today's date)
  //  2. future blocks
  //  3. past blocks
  const flexWorkoutsGrouped = flexWorkouts.reduce((acc, workout) => {
    const block = workout.block;
    if (!block) return acc;
    if (!acc[block.id]) {
      acc[block.id] = [];
    }
    acc[block.id]!.push(workout);
    return acc;
  }, {} as Record<string, WorkoutData[]>);

  const toDateOnly = (value: string | null | undefined) =>
    value ? value.slice(0, 10) : '';

  // 0 = current, 1 = future, 2 = past
  const getBlockTemporalRank = (
    block: NonNullable<WorkoutData['block']> | null | undefined,
  ) => {
    const startDate = toDateOnly(block?.startDate);
    const endDate = toDateOnly(block?.endDate);
    if (startDate && endDate && startDate <= today && today <= endDate) {
      return 0;
    }
    if (startDate && startDate > today) {
      return 1;
    }
    return 2;
  };

  const flexWorkoutsSorted = Object.values(flexWorkoutsGrouped).sort((a, b) => {
    const aBlock = a[0]?.block;
    const bBlock = b[0]?.block;
    const rankDiff = getBlockTemporalRank(aBlock) - getBlockTemporalRank(bBlock);
    if (rankDiff !== 0) return rankDiff;
    return toDateOnly(aBlock?.startDate).localeCompare(
      toDateOnly(bBlock?.startDate),
    );
  });
  // sort the workouts by session x cycle numbers ascending
  const flexWorkoutsSortedBySessionAndCycle = flexWorkoutsSorted.map((block) => {
    return sortByCycleAndSession(block);
  });

  const sortByDate = <T extends WorkoutData>(
    workouts: T[],
    field: keyof T,
    desc = false,
  ) =>
    [...workouts].sort((a, b) => {
      const aTime = a[field] ? new Date(a[field] as string).getTime() : 0;
      const bTime = b[field] ? new Date(b[field] as string).getTime() : 0;
      const diff = aTime - bTime;
      return desc ? -diff : diff;
    });

  return {
    today: sortByDate(todayWorkouts, 'assignedDate'),
    missing: sortByDate(missingWorkouts, 'assignedDate', true),
    upcoming: sortByDate(upcomingWorkouts, 'assignedDate'),
    flex: flexWorkoutsSortedBySessionAndCycle.flat(),
  };
}

export function getUnrecordedActuals(workout: Workout | undefined) {
  const unrecorded: UnrecordedActual[] = [];

  if (!workout) return unrecorded;

  function addUnrecordedActuals({
    metrics,
    sets,
    workoutExerciseId,
    exerciseName,
  }: WorkoutExercise) {
    const metricIds = metrics.map((m) => m.metricId);
    if (
      sets.some((s) =>
        s.metrics.some(
          (m) => metricIds.includes(m.metricId) && m.input.trim() === '',
        ),
      )
    ) {
      unrecorded.push({ workoutExerciseId, exerciseName });
    }
  }

  workout?.sections.forEach((section) => {
    section.entries.forEach((entry) => {
      if (entry.__type === 'WorkoutExercise') {
        addUnrecordedActuals(entry);
      } else if (entry.__type === 'WorkoutSuperset') {
        entry.exercises.forEach((exercise) => {
          addUnrecordedActuals(exercise.exercise);
        });
      } else {
        const selectedExercise = entry.exercises.find(
          (e) => e.workoutExerciseId === entry.selectedExerciseId,
        );
        if (selectedExercise) {
          // We only care about unrecorded metrics for the alternate exercise selected
          addUnrecordedActuals(selectedExercise.exercise);
        }
      }
    });
  });

  return unrecorded;
}
